// src/pages/public/PublicBookingPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

const isIp = (hostname) => /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);

const slugFromHost = () => {
  const hostname = window.location.hostname; // fără port

  // pe localhost sau IP (ex: 169.254.x.x) NU putem deduce slug
  if (!hostname || hostname === "localhost" || isIp(hostname)) return "";

  const parts = hostname.split(".");
  // {slug}.domain.tld  (minim 3 segmente)
  if (parts.length >= 3) return parts[0];

  return "";
};

export default function PublicBookingPage() {
  const { t } = useTranslation();
  const { businessSlug } = useParams();

  // ✅ slug efectiv: din /p/:businessSlug (dev) sau din subdomeniu (prod)
  const effectiveSlug = useMemo(
    () => (businessSlug && String(businessSlug).trim()) || slugFromHost(),
    [businessSlug]
  );

  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [error, setError] = useState("");

  // wizard state
  const [step, setStep] = useState(1);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedDate, setSelectedDate] = useState(""); // YYYY-MM-DD
  const [selectedTime, setSelectedTime] = useState(""); // HH:MM
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [notes, setNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  /* =====================================
   * 0) Root fără slug în dev -> du-te la login (NU public booking)
   * ===================================== */
  useEffect(() => {
    const hostname = window.location.hostname;
    const cannotInferSlugFromHost =
      !hostname || hostname === "localhost" || isIp(hostname);

    // Dacă suntem pe host fără subdomeniu și nu avem /p/:businessSlug,
    // atunci public booking nu are sens -> redirect la /app/login
    if (cannotInferSlugFromHost && !(businessSlug && String(businessSlug).trim())) {
      window.location.replace("/app/login");
    }
  }, [businessSlug]);

  /* =====================================
   * 1) Load business + services
   * ===================================== */
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      setSubmitResult(null);

      // dacă nu putem determina slug-ul, afișăm mesaj util
      // (pe localhost root deja redirectăm mai sus, asta e pentru linkuri greșite)
      if (!effectiveSlug) {
        setBusiness(null);
        setServices([]);
        setError(t("publicBooking.missingSlugDev"));
        setLoading(false);
        return;
      }

      try {
        const [bizRes, svcRes] = await Promise.all([
          api.get("/public/business", {
            params: { business_slug: effectiveSlug },
          }),
          api.get("/public/services", {
            params: { business_slug: effectiveSlug },
          }),
        ]);

        if (cancelled) return;

        const biz = bizRes.data || null;

        // ✅ dacă public booking nu e activat pentru business → du-te la app login
        if (biz && biz.public_booking_enabled === false) {
          window.location.replace("/app/login");
          return;
        }

        setBusiness(biz);
        setServices(Array.isArray(svcRes.data) ? svcRes.data : []);
      } catch (e) {
        if (cancelled) return;
        console.error("Public booking load error:", e?.response || e);
        setBusiness(null);
        setServices([]);
        setError(t("publicBooking.loadBusinessError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [effectiveSlug]);

  // serviciul selectat
  const selectedService = useMemo(
    () =>
      services.find((s) => String(s.id) === String(selectedServiceId || "")) ||
      null,
    [services, selectedServiceId]
  );

  // mărimea slotului: durata serviciului sau fallback la 30 min
  const slotMinutes = selectedService?.duration_minutes || 30;

  // timepicker simplu, 09:00–17:00 din slot în slot
  const timeOptions = useMemo(() => {
    const startMinutes = 9 * 60;
    const endMinutes = 17 * 60;
    const opts = [];
    for (let m = startMinutes; m + slotMinutes <= endMinutes; m += slotMinutes) {
      const hh = String(Math.floor(m / 60)).padStart(2, "0");
      const mm = String(m % 60).padStart(2, "0");
      opts.push(`${hh}:${mm}`);
    }
    return opts;
  }, [slotMinutes]);

  // azi în format YYYY-MM-DD (pt min la date)
  const todayISO = useMemo(() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  // când ajungi în pasul 2 prima dată, propune azi ca dată
  useEffect(() => {
    if (!selectedDate && step >= 2) setSelectedDate(todayISO);
  }, [step, selectedDate, todayISO]);

  /* =========================
   * 2. Step handlers
   * ========================= */
  const goNextFromStep1 = () => {
    if (!selectedServiceId) {
      setError(t("publicBooking.chooseServiceFirst"));
      setTimeout(() => setError(""), 3000);
      return;
    }
    setError("");
    setStep(2);
  };

  const goNextFromStep2 = () => {
    if (!selectedDate || !selectedTime) {
      setError(t("publicBooking.chooseDateAndTime"));
      setTimeout(() => setError(""), 3000);
      return;
    }
    setError("");
    setStep(3);
  };

  /* =========================
   * 3. Submit programare
   * ========================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedService || !selectedDate || !selectedTime) return;

    if (!clientName.trim() || !clientPhone.trim()) {
      setError(t("publicBooking.namePhoneRequired"));
      setTimeout(() => setError(""), 3000);
      return;
    }

    if (!effectiveSlug) {
      setError(t("publicBooking.missingSlug"));
      setTimeout(() => setError(""), 3000);
      return;
    }

    setSubmitting(true);
    setError("");
    setSubmitResult(null);

    try {
      const startISO = new Date(`${selectedDate}T${selectedTime}:00`).toISOString();

      const res = await api.post("/public/appointments", {
        business_slug: effectiveSlug,
        service_id: selectedService.id,
        start: startISO,
        client_name: clientName.trim(),
        client_phone: clientPhone.trim(),
        client_email: clientEmail.trim() || undefined,
        notes: notes.trim() || undefined,
      });

      const checkoutUrl = res?.data?.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }

      setSubmitResult({
        ok: true,
        message: t("publicBooking.successMessage"),
      });

      setStep(1);
      setSelectedServiceId("");
      setSelectedDate("");
      setSelectedTime("");
      setClientName("");
      setClientPhone("");
      setClientEmail("");
      setNotes("");
    } catch (e2) {
      console.error("Public booking submit error:", e2?.response || e2);

      let detail =
        e2?.response?.data?.detail ||
        e2?.response?.data ||
        t("publicBooking.submitError");

      if (typeof detail !== "string") {
        detail = t("publicBooking.submitErrorContact");
      } else if (detail.includes("<html") || detail.length > 400) {
        detail = t("publicBooking.internalError");
      }

      setSubmitResult({ ok: false, message: detail });
    } finally {
      setSubmitting(false);
    }
  };

  /* =========================
   * 4. Render
   * ========================= */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-sm text-slate-600">{t("publicBooking.loadingPage")}</div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="max-w-md w-full bg-white shadow rounded-xl px-6 py-5 text-center">
          <h1 className="text-lg font-semibold mb-2">{t("publicBooking.pageTitle")}</h1>
          <p className="text-sm text-slate-600 mb-4">
            {error || t("publicBooking.businessNotFoundMsg")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full bg-white shadow-lg rounded-2xl px-6 py-6 md:px-8 md:py-8">
        <div className="mb-6">
          <div className="text-xs font-medium tracking-wide text-slate-500 uppercase mb-1">
            {t("publicBooking.pageTitle")}
          </div>
          <h1 className="text-2xl font-semibold mb-1">{business.name || business.slug}</h1>
          <div className="text-sm text-slate-500">
            {business.country || "RO"} • {business.locale || "ro-RO"}
          </div>
        </div>

        <ol className="flex items-center gap-4 text-xs mb-6">
          <li
            className={classNames(
              "flex items-center gap-2",
              step >= 1 ? "text-slate-900" : "text-slate-400"
            )}
          >
            <span
              className={classNames(
                "w-6 h-6 rounded-full flex items-center justify-center text-[11px] border",
                step >= 1
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-400 border-slate-300"
              )}
            >
              1
            </span>
            <span>{t("publicBooking.step1Label")}</span>
          </li>
          <li
            className={classNames(
              "flex items-center gap-2",
              step >= 2 ? "text-slate-900" : "text-slate-400"
            )}
          >
            <span
              className={classNames(
                "w-6 h-6 rounded-full flex items-center justify-center text-[11px] border",
                step >= 2
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-300 border-slate-300"
              )}
            >
              2
            </span>
            <span>{t("publicBooking.step2Label")}</span>
          </li>
          <li
            className={classNames(
              "flex items-center gap-2",
              step >= 3 ? "text-slate-900" : "text-slate-400"
            )}
          >
            <span
              className={classNames(
                "w-6 h-6 rounded-full flex items-center justify-center text-[11px] border",
                step >= 3
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-300 border-slate-300"
              )}
            >
              3
            </span>
            <span>{t("publicBooking.step3Label")}</span>
          </li>
        </ol>

        {error && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </div>
        )}

        {submitResult && (
          <div
            className={classNames(
              "mb-4 text-sm rounded-lg px-3 py-2 border",
              submitResult.ok
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-red-50 border-red-200 text-red-700"
            )}
          >
            {submitResult.message}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                1. {t("publicBooking.step1Label")}
              </label>
              <select
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/80"
                value={selectedServiceId || ""}
                onChange={(e) => setSelectedServiceId(e.target.value)}
              >
                <option value="">{t("publicBooking.selectServicePlaceholder")}</option>
                {services.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} {s.duration_minutes ? `• ${s.duration_minutes} min` : ""}
                  </option>
                ))}
              </select>
              {services.length === 0 && (
                <p className="mt-2 text-xs text-slate-500">
                  {t("publicBooking.noPublicServices")}
                </p>
              )}
            </div>

            {selectedService && (
              <p className="text-xs text-slate-500">
                {t("publicBooking.selectedServiceMsg")} <strong>{selectedService.name}</strong>.
              </p>
            )}

            <div className="pt-2 flex justify-end">
              <button
                type="button"
                disabled={!selectedServiceId}
                onClick={goNextFromStep1}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-900 text-white disabled:opacity-40"
              >
                {t("publicBooking.continueBtn")}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <div>
                <div className="font-medium mb-1">2. {t("publicBooking.step2Label")}</div>
                {selectedService && (
                  <div className="text-xs text-slate-500">
                    {t("publicBooking.serviceLabelInfo")} <strong>{selectedService.name}</strong>{" "}
                    {selectedService.duration_minutes
                      ? `(${selectedService.duration_minutes} min)`
                      : null}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="text-xs text-slate-500 underline"
                onClick={() => setStep(1)}
              >
                {t("publicBooking.changeService")}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {t("publicBooking.dateLabel")}
                </label>
                <input
                  type="date"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={selectedDate || ""}
                  min={todayISO}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {t("publicBooking.timeLabel")}
                </label>
                <select
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={selectedTime || ""}
                  onChange={(e) => setSelectedTime(e.target.value)}
                >
                  <option value="">{t("publicBooking.chooseTime")}</option>
                  {timeOptions.map((slot) => (
                    <option key={slot} value={slot}>
                      {slot}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-[11px] text-slate-500">
                  {t("publicBooking.standardHours")}
                </p>
              </div>
            </div>

            <div className="pt-2 flex justify-between">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="px-3 py-2 rounded-lg text-xs border border-slate-300 text-slate-700"
              >
                {t("publicBooking.backBtn")}
              </button>

              <button
                type="button"
                disabled={!selectedDate || !selectedTime}
                onClick={goNextFromStep2}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-900 text-white disabled:opacity-40"
              >
                {t("publicBooking.continueBtn")}
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <div>
                <div className="font-medium mb-1">3. {t("publicBooking.step3Label")}</div>
                <div className="text-xs text-slate-500">
                  {selectedService && (
                    <>
                      {t("publicBooking.serviceLabelInfo")} <strong>{selectedService.name}</strong> ·{" "}
                    </>
                  )}
                  {t("publicBooking.dateLabel")}: <strong>{selectedDate}</strong> · {t("publicBooking.timeLabel")}:{" "}
                  <strong>{selectedTime}</strong>
                </div>
              </div>
              <button
                type="button"
                className="text-xs text-slate-500 underline"
                onClick={() => setStep(2)}
              >
                {t("publicBooking.changeDateTime")}
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {t("publicBooking.fullNameLabel")}
                </label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="Ex: Ana Popescu"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {t("publicBooking.phoneLabel")}
                </label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="Ex: 07xx xxx xxx"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  {t("publicBooking.emailLabel")}
                </label>
                <input
                  type="email"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="Ex: ana@example.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                {t("publicBooking.notesObservationsLabel")}
              </label>
              <textarea
                rows={3}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t("publicBooking.notesPlaceholder")}
              />
            </div>

            <div className="pt-2 flex justify-between">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-3 py-2 rounded-lg text-xs border border-slate-300 text-slate-700"
              >
                {t("publicBooking.backBtn")}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-900 text-white disabled:opacity-40"
              >
                {submitting ? t("publicBooking.submittingBtn") : t("publicBooking.confirmBooking")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

